import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import {
  CreateUserRequestSchema,
  HttpProblemError,
  type CreateUserRequest,
  type TemporaryPasswordResult,
  type User,
  type UserRole,
} from '@collector/shared'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { formatDateTime } from '../lib/format'
import { logError } from '../lib/log'
import { PageHeading } from '../components/PageHeading'

const ALL_ROLES: UserRole[] = ['user', 'admin', 'developer']

export function UsersPage() {
  const { t } = useTranslation()
  const auth = useAuth()
  const queryClient = useQueryClient()
  const [serverError, setServerError] = useState<string | null>(null)
  const [tempPassword, setTempPassword] = useState<TemporaryPasswordResult | null>(null)
  const users = useQuery({ queryKey: ['users'], queryFn: () => api().users.list() })

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateUserRequest>({
    resolver: zodResolver(CreateUserRequestSchema),
    defaultValues: { roles: ['user'] },
  })
  const selectedRoles = watch('roles') ?? []

  const create = async (values: CreateUserRequest) => {
    setServerError(null)
    try {
      const result = await api().users.create(values)
      void queryClient.invalidateQueries({ queryKey: ['users'] })
      reset({ email: '', roles: ['user'] })
      setTempPassword(result)
    } catch (err) {
      setServerError(
        err instanceof HttpProblemError ? err.problem.title : t('common.unexpectedError'),
      )
    }
  }

  const resetUserPassword = useMutation({
    mutationFn: (id: string) => api().users.resetPassword(id),
    onSuccess: (result) => {
      setServerError(null)
      setTempPassword(result)
      void queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (err) => {
      setServerError(
        err instanceof HttpProblemError ? err.problem.title : t('common.unexpectedError'),
      )
    },
  })

  const updateRoles = useMutation({
    mutationFn: ({ id, roles }: { id: string; roles: UserRole[] }) =>
      api().users.patch(id, { roles }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  const updateContact = useMutation({
    mutationFn: ({ id, ...patch }: { id: string; username?: string; email?: string }) =>
      api().users.patch(id, patch),
    onSuccess: () => {
      setServerError(null)
      void queryClient.invalidateQueries({ queryKey: ['users'] })
    },
    onError: (err) => {
      setServerError(
        err instanceof HttpProblemError ? err.problem.title : t('common.unexpectedError'),
      )
      // Refresh the list so the inline editor reverts to the persisted value.
      void queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  const remove = useMutation({
    mutationFn: (id: string) => api().users.delete(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  return (
    <div>
      <PageHeading title={t('users.title')} description={t('users.description')} />
      <form
        className="card mb-6 grid grid-cols-1 items-end gap-3 sm:grid-cols-3"
        onSubmit={(e) => {
          void handleSubmit(create)(e)
        }}
      >
        <div className="sm:col-span-2">
          <label className="label">{t('users.email')}</label>
          <input className="input" type="email" {...register('email')} />
          {errors.email ? (
            <p className="text-xs text-red-600 dark:text-red-400">{errors.email.message}</p>
          ) : null}
        </div>
        <div>
          <label className="label">{t('users.roles')}</label>
          <RoleCheckboxes
            value={selectedRoles}
            onChange={(next) => {
              setValue('roles', next, { shouldValidate: true })
            }}
          />
          {errors.roles ? (
            <p className="text-xs text-red-600 dark:text-red-400">{errors.roles.message}</p>
          ) : null}
        </div>
        {serverError ? (
          <p className="text-sm text-red-600 sm:col-span-3 dark:text-red-400">{serverError}</p>
        ) : null}
        <div className="sm:col-span-3 sm:text-right">
          <button type="submit" className="btn-primary" disabled={isSubmitting}>
            {t('users.create')}
          </button>
        </div>
      </form>

      <div className="overflow-x-auto rounded-md border border-stone-200 bg-white dark:border-stone-700 dark:bg-stone-900">
        <table className="min-w-full divide-y divide-stone-200 text-sm dark:divide-stone-700">
          <thead>
            <tr className="table-header">
              <th className="px-3 py-2">{t('users.username')}</th>
              <th className="px-3 py-2">{t('users.email')}</th>
              <th className="px-3 py-2">{t('users.roles')}</th>
              <th className="px-3 py-2">{t('users.created')}</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100 dark:divide-stone-800">
            {users.data?.map((u) => (
              <UserRow
                key={u.id}
                user={u}
                isSelf={auth.user?.id === u.id}
                onUsernameChange={(username) => {
                  updateContact.mutate({ id: u.id, username })
                }}
                onEmailChange={(email) => {
                  updateContact.mutate({ id: u.id, email })
                }}
                onRolesChange={(roles) => {
                  updateRoles.mutate({ id: u.id, roles })
                }}
                onResetPassword={() => {
                  if (window.confirm(t('users.confirmResetPassword', { username: u.username }))) {
                    resetUserPassword.mutate(u.id)
                  }
                }}
                onDelete={() => {
                  if (window.confirm(t('users.confirmDelete', { username: u.username }))) {
                    remove.mutate(u.id)
                  }
                }}
              />
            ))}
          </tbody>
        </table>
      </div>

      {tempPassword ? (
        <TemporaryPasswordModal
          result={tempPassword}
          onClose={() => {
            setTempPassword(null)
          }}
        />
      ) : null}
    </div>
  )
}

interface UserRowProps {
  user: User
  isSelf: boolean
  onUsernameChange: (username: string) => void
  onEmailChange: (email: string) => void
  onRolesChange: (roles: UserRole[]) => void
  onResetPassword: () => void
  onDelete: () => void
}

function UserRow({
  user,
  isSelf,
  onUsernameChange,
  onEmailChange,
  onRolesChange,
  onResetPassword,
  onDelete,
}: UserRowProps) {
  const { t } = useTranslation()
  return (
    <tr>
      <td className="px-3 py-2">
        <div className="flex items-center gap-1.5">
          <EditableTextCell value={user.username} onCommit={onUsernameChange} />
          {user.mustChangePassword ? (
            <span
              className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900 dark:text-amber-100"
              title={t('users.mustChangePasswordHint')}
            >
              {t('users.mustChangePasswordBadge')}
            </span>
          ) : null}
        </div>
      </td>
      <td className="px-3 py-2">
        <EditableTextCell value={user.email} onCommit={onEmailChange} type="email" />
      </td>
      <td className="px-3 py-2">
        <RoleCheckboxes value={user.roles} onChange={onRolesChange} />
      </td>
      <td className="px-3 py-2 text-stone-600 dark:text-stone-400">
        {formatDateTime(user.createdAt)}
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex items-center justify-end gap-2">
          <button className="btn-secondary" onClick={onResetPassword}>
            {t('users.resetPassword')}
          </button>
          {isSelf ? (
            <span className="text-xs text-stone-500 dark:text-stone-400">
              {t('common.youSuffix')}
            </span>
          ) : (
            <button className="btn-danger" onClick={onDelete}>
              {t('common.delete')}
            </button>
          )}
        </div>
      </td>
    </tr>
  )
}

interface TemporaryPasswordModalProps {
  result: TemporaryPasswordResult
  onClose: () => void
}

function TemporaryPasswordModal({ result, onClose }: TemporaryPasswordModalProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.temporaryPassword)
      setCopied(true)
    } catch (err) {
      // The operator can still read the password from the modal — log so
      // we notice if browser permission policies start blocking the copy.
      logError('users.tempPassword.copy_failed', err)
    }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/50 px-4">
      <div className="w-full max-w-md space-y-4 rounded-lg border border-stone-200 bg-white p-6 shadow-lg dark:border-stone-700 dark:bg-stone-900">
        <div>
          <h2 className="text-base font-semibold text-stone-900 dark:text-stone-100">
            {t('users.tempPasswordTitle')}
          </h2>
          <p className="text-sm text-stone-500 dark:text-stone-400">
            {t('users.tempPasswordBody', { username: result.user.username })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <code className="flex-1 select-all rounded border border-stone-300 bg-stone-50 px-3 py-2 font-mono text-sm text-stone-900 dark:border-stone-600 dark:bg-stone-800 dark:text-stone-100">
            {result.temporaryPassword}
          </code>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => {
              void copy()
            }}
          >
            {copied ? t('tablets.copied') : t('tablets.copySecret')}
          </button>
        </div>
        <p className="text-xs text-stone-500 dark:text-stone-400">
          {t('users.tempPasswordWarning')}
        </p>
        <div className="text-right">
          <button type="button" className="btn-primary" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  )
}

interface EditableTextCellProps {
  value: string
  onCommit: (next: string) => void
  type?: 'text' | 'email'
}

function EditableTextCell({ value, onCommit, type = 'text' }: EditableTextCellProps) {
  const [draft, setDraft] = useState(value)
  // Keep the draft in sync when the row re-renders with a fresh server value
  // (e.g. after a successful patch). React Query will rebuild the row, which
  // remounts this component anyway — useState's initialiser handles the sync.
  const commit = () => {
    const next = draft.trim()
    if (next.length === 0 || next === value) {
      setDraft(value)
      return
    }
    onCommit(next)
  }
  return (
    <input
      className="input w-full"
      type={type}
      value={draft}
      onChange={(e) => {
        setDraft(e.target.value)
      }}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          commit()
        } else if (e.key === 'Escape') {
          setDraft(value)
          ;(e.target as HTMLInputElement).blur()
        }
      }}
    />
  )
}

interface RoleCheckboxesProps {
  value: UserRole[]
  onChange: (next: UserRole[]) => void
}

function RoleCheckboxes({ value, onChange }: RoleCheckboxesProps) {
  const { t } = useTranslation()
  const toggle = (role: UserRole) => {
    const next = value.includes(role) ? value.filter((r) => r !== role) : [...value, role]
    // The API enforces at least one role, but catching it client-side gives
    // the operator a tighter feedback loop than a 400 round-trip.
    if (next.length === 0) return
    onChange(next)
  }
  return (
    <div className="flex flex-wrap gap-2">
      {ALL_ROLES.map((role) => {
        const checked = value.includes(role)
        return (
          <label
            key={role}
            className={`cursor-pointer select-none rounded border px-2 py-1 text-xs font-medium ${
              checked
                ? 'border-emerald-800 bg-emerald-800 text-white'
                : 'border-stone-300 bg-white text-stone-700 hover:bg-stone-100 dark:border-stone-600 dark:bg-stone-900 dark:text-stone-200 dark:hover:bg-stone-800'
            }`}
          >
            <input
              type="checkbox"
              className="sr-only"
              checked={checked}
              onChange={() => {
                toggle(role)
              }}
            />
            {t(`users.rolesOptions.${role}`)}
          </label>
        )
      })}
    </div>
  )
}
